import { EmeraldErrorModal } from 'src/common/api/emerald-error';
import { EmeraldDialogTypes } from 'src/common/enumerations/emerald-dialog-types';
import XLRLogger from 'src/common/helpers/logger';
import { popupMessage } from 'src/messages/business-logic/popup-message';
import { getCurrentXLRConfig } from 'src/xlr-configuration/xlr-config';
import { ISellableTenders } from '../../common/interfaces/dtos/ISellableTenders';
import { ITender, TenderPaymentOptions } from '../../common/interfaces/dtos/ITender';
import { ITenderGroup } from '../../common/interfaces/dtos/ITenderGroup';
import { store } from '../../common/redux/configure-store';
import { TenderMenuActions } from '../../common/redux/reducers/tender-menu-reducer';
import { getPaybackSellableTenders } from './service/get-payback-sellable-tenders';
import { getTenderPaymentOptions } from './service/get-tender-payment-options';
import { getValidSellableTender } from './service/get-valid-sellable-tenders';
import { tenderMenuConfigurationLookup } from './service/menu-configuration-lookup';

export const getPaymentOptions = async (
  tenders?: ITender[],
  isRefund: boolean = false,
  updateTenderStore: boolean = true,
): Promise<void> => {
  if (getCurrentXLRConfig().dynamicTender || isRefund) {
    return getTenders(tenders, isRefund)
      .then(getAndSetTenderGroups)
      .then(updateTenderStore ? updateTenderMenu : () => {})
      .catch(err => {
        XLRLogger.error({
          message: 'Failed to get payment options',
          data: err,
          source: 'get-payment-options.ts',
        });
      });
  }
};

export const getPaymentTenderOptions = async (tenderId?: string): Promise<TenderPaymentOptions> => {
  const tenderFromStore = store
    .getState()
    .tenderMenu.sellableTenders.tenderGroups.flatMap(tenderGroup => tenderGroup.tenders)
    .find(tender => tender.id === tenderId);

  if (tenderFromStore?.paymentOptions) {
    return tenderFromStore.paymentOptions;
  } else {
    if (!getCurrentXLRConfig().dynamicTender) {
      popupMessage(
        new EmeraldErrorModal({
          type: EmeraldDialogTypes.Error,
          message: 'TendersMisconfiguredInStore',
          subMessage: 'InvalidPaymentOptionsSubMessage',
          acceptButtonLabel: 'Close',
        }),
      );
    }
    return getTenderPaymentOptions(tenderId);
  }
};

const getTenders = async (
  tenders?: ITender[],
  isRefund: boolean = false,
): Promise<ISellableTenders> => {
  tenders =
    tenders ||
    (isRefund
      ? (await getPaybackSellableTenders()).refundableTenders
      : await getValidSellableTender());
  return {
    tenderGroups: [],
    rawTenders: tenders || [],
  };
};

export const updateTenderMenu = (sellableTenders: ISellableTenders): void => {
  store.dispatch(TenderMenuActions.setTenderMenu(sellableTenders));
};

export const getAndSetTenderGroups = async (
  sellableTenders: ISellableTenders,
): Promise<ISellableTenders> => {
  const tenderGroups = store.getState().tenderMenu.sellableTenders.tenderGroups;
  const hasTenderGroups = !!tenderGroups.length;
  const tenderIds = sellableTenders.rawTenders.map((t): string => t.id);

  if (!hasTenderGroups) {
    const manualTouchPointType =
      store.getState().touchPoint.touchPointConfiguration.manualTouchPointType ?? '';
    sellableTenders.tenderGroups = await tenderMenuConfigurationLookup(
      manualTouchPointType,
      tenderIds,
    );
  } else {
    sellableTenders.tenderGroups = tenderGroups;
  }

  setTendersInTenderGroup(sellableTenders);
  setSingleTenderGroupsName(sellableTenders.tenderGroups);

  if (!hasTenderGroups) {
    setEftGroups(sellableTenders);
    setAtmGroups(sellableTenders);
  }
  return sellableTenders;
};

const setTendersInTenderGroup = (sellableTenders: ISellableTenders): void => {
  if (sellableTenders.rawTenders?.length && sellableTenders.tenderGroups) {
    sellableTenders.tenderGroups.forEach((tenderGroup): void => {
      tenderGroup.tenders.forEach((tenderInGroup, index, array): void => {
        const td = sellableTenders.rawTenders.find(
          (tender): boolean => tender.id === tenderInGroup.id,
        );
        if (td) {
          array[index] = td;
        } else {
          tenderInGroup.isActive = false;
        }
      });
    });
  }
};

const setAtmGroups = (sellableTenders: ISellableTenders) => {
  sellableTenders.tenderGroups.forEach((tenderGroup): void => {
    if (countProceededByAtm(tenderGroup) === tenderGroup.tenders.length) {
      tenderGroup.isAtmGroup = true;
    }
  });
};

const countProceededByAtm = (tenderGroup: ITenderGroup): number => {
  const atmGroup = tenderGroup.tenders.filter((td): boolean => td.isProcessedByAtm === true);
  return atmGroup.length;
};

const setEftGroups = (sellableTenders: ISellableTenders) => {
  sellableTenders.tenderGroups.forEach((tenderGroup): void => {
    if (countProceededByEps(tenderGroup) === tenderGroup.tenders.length) {
      tenderGroup.isEftGroup = true;
    }
  });
};

const countProceededByEps = (tenderGroup: ITenderGroup): number => {
  const epsGroup = tenderGroup.tenders.filter((td): boolean => td.isProcessedByEps === true);
  return epsGroup.length;
};

const setSingleTenderGroupsName = (tenderGroups: ITenderGroup[]): void => {
  tenderGroups.forEach((tg): void => {
    if (tg.tenders.length === 1) {
      tg.name = tg.tenders[0].description;
      tg.image = tg.image || tg.tenders[0].image;
    }
  });
};
